import { ChangeEvent, CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import { AppDialog, FormField, FormRow, FormToggle } from "../../../components/ui";
import {
  MAX_POINT_REWARD_COST,
  MAX_QR_EXPIRATION_HOURS,
  MIN_POINT_REWARD_COST,
  MIN_QR_EXPIRATION_HOURS,
  POINT_REWARD_TYPE_LABELS,
  PointReward,
  PointRewardType,
  formatPendingRedemptionsNotice,
} from "../../../models/pointReward";
import {
  useCreatePointRewardMutation,
  useUpdatePointRewardMutation,
} from "../../../store/services/point-rewards";
import { IMAGE_FILE_ACCEPT, validateImageFile } from "../../../utils/functions/imageFileValidation";
import { getApiErrorMessage } from "../../../utils/functions/api-errors";
import { useGetBenefitPartnersQuery } from "../../../store/services/benefits";
import { uploadImageToS3 } from "../../../utils/functions/uploadToS3";
import { POINT_RULE_BLOCK_TITLE_STYLE } from "../../PointRules/components/PointRulePreview";
import { normalizeText } from "../../PointRules/components/pointRuleForm";
import { PointRewardPreview } from "./PointRewardPreview";
import {
  POINT_REWARD_IMAGE_UPLOAD_FOLDER,
  PointRewardFormErrors,
  PointRewardFormValues,
  buildCreatePointRewardPayload,
  buildInitialPointRewardFormValues,
  buildPointRewardChanges,
  validatePointRewardForm,
} from "./pointRewardForm";

export interface PointRewardFormDialogProps {
  visible: boolean;
  onHide: () => void;
  tenantId: string;
  /** null = alta. */
  reward: PointReward | null;
}

const EDIT_NOTICE =
  "Los cambios aplican a canjes futuros: los canjes ya hechos conservan su costo y su vencimiento.";
const CREATE_SUCCESS_TOAST = "Premio creado.";
const UPDATE_SUCCESS_TOAST = "Premio actualizado.";
const SAVE_FALLBACK_MESSAGE = "No se pudo guardar el premio";
const IMAGE_UPLOAD_ERROR_MESSAGE = "No se pudo subir la imagen";
const IMAGE_HINT = "PNG, JPG o WebP · máx. 5 MB";
const IMAGE_REMOVE_HINT = "La imagen se quita al guardar";
const STOCK_HINT = "0 = agotado";
const QR_EXPIRATION_HINT =
  "Cuánto tiempo tiene la persona para usar el QR desde que canjea. Máximo 720 horas (30 días).";

const REWARD_TYPE_OPTIONS = (Object.keys(POINT_REWARD_TYPE_LABELS) as PointRewardType[]).map(
  (value) => ({ label: POINT_REWARD_TYPE_LABELS[value], value })
);

const IMAGE_THUMBNAIL_STYLE: CSSProperties = {
  display: "block",
  width: "12rem",
  aspectRatio: "16 / 9",
  objectFit: "cover",
  borderRadius: "var(--radius-md)",
};

/** §5.1 — separación de secciones de formulario: 16px. */
const BlockTitle = ({ children }: { children: ReactNode }) => (
  <h3 style={{ ...POINT_RULE_BLOCK_TITLE_STYLE, margin: "var(--space-16) 0" }}>{children}</h3>
);

/**
 * Alta y edición de un premio del catálogo de canjes. En edición solo se envían los campos
 * que cambiaron. La imagen se sube recién al guardar y solo si se eligió un archivo nuevo.
 */
export const PointRewardFormDialog = ({
  visible,
  onHide,
  tenantId,
  reward,
}: PointRewardFormDialogProps) => {
  const [values, setValues] = useState<PointRewardFormValues>(() =>
    buildInitialPointRewardFormValues(reward)
  );
  const [attempted, setAttempted] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Cada apertura (o cambio de premio) arranca de los valores guardados.
  useEffect(() => {
    if (!visible) return;
    setValues(buildInitialPointRewardFormValues(reward));
    setAttempted(false);
  }, [visible, reward]);

  // Vista previa local del archivo elegido (mismo patrón que BenefitPartnerForm).
  const localPreviewUrl = useMemo(
    () => (values.imageFile ? URL.createObjectURL(values.imageFile) : null),
    [values.imageFile]
  );
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const [updatePointReward, updateState] = useUpdatePointRewardMutation();
  const [createPointReward, createState] = useCreatePointRewardMutation();

  // Partner de Beneficios del mismo tenant: el backend rechaza cualquier otro.
  const { data: partners, isLoading: isLoadingPartners } = useGetBenefitPartnersQuery(
    { tenantId },
    { skip: !visible }
  );
  const partnerOptions = useMemo(
    () =>
      (partners ?? [])
        .map((partner) => ({ label: partner.name ?? "Sin nombre", value: partner.id }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [partners]
  );
  const selectedPartnerName =
    values.partnerId === null
      ? null
      : partnerOptions.find((option) => option.value === values.partnerId)?.label ??
        reward?.partnerName ??
        null;
  // Premios cargados antes de que el partner fuera una referencia: el nombre quedó como texto
  // y no se pudo vincular a un partner de Beneficios.
  const legacyPartnerHint =
    reward && reward.partnerId === null && reward.partnerName
      ? `Tenía cargado "${reward.partnerName}" como texto. Elegí el partner de la lista.`
      : undefined;

  const setField = <K extends keyof PointRewardFormValues>(
    field: K,
    value: PointRewardFormValues[K]
  ) => setValues((previous) => ({ ...previous, [field]: value }));

  // El campo Stock queda siempre habilitado: escribir un número apaga "Stock ilimitado", y
  // prender "Stock ilimitado" borra el número. Antes el campo arrancaba deshabilitado y el
  // switch que lo habilitaba estaba en la fila de abajo, así que parecía que no aceptaba números.
  const setStock = (stock: number | null) =>
    setValues((previous) => ({
      ...previous,
      stock,
      unlimitedStock: stock === null ? previous.unlimitedStock : false,
    }));

  // InputNumber de PrimeReact: onChange avisa en cada tecla y onValueChange recién al salir del
  // campo. Con solo onValueChange, mientras se escribe el form no ve el cambio y "Guardar cambios"
  // se ve deshabilitado. Cada campo usa el mismo handler para los dos eventos.
  const handlePointsCostChange = (event: { value?: number | null }) =>
    setField("pointsCost", event.value ?? null);
  const handleStockChange = (event: { value?: number | null }) => setStock(event.value ?? null);
  const handleQrExpirationHoursChange = (event: { value?: number | null }) =>
    setField("qrExpirationHours", event.value ?? null);

  const setUnlimitedStock = (unlimitedStock: boolean) =>
    setValues((previous) => ({
      ...previous,
      unlimitedStock,
      stock: unlimitedStock ? null : previous.stock,
    }));

  const errors = validatePointRewardForm(values);
  const shownErrors: PointRewardFormErrors = attempted ? errors : {};
  const hasErrors = Object.keys(errors).length > 0;

  // Sin cambios (y sin archivo nuevo) no hay nada que guardar. Con errores el botón queda
  // habilitado para que el click muestre qué falta.
  const changes = reward ? buildPointRewardChanges(reward, values) : null;
  const primaryDisabled =
    changes !== null &&
    values.imageFile === null &&
    !hasErrors &&
    Object.keys(changes).length === 0;

  const isSaving = isUploadingImage || (reward ? updateState.isLoading : createState.isLoading);

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Se vacía para poder volver a elegir el mismo archivo.
    event.target.value = "";
    if (!file) return;
    const fileError = validateImageFile(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }
    setField("imageFile", file);
  };

  const handleSave = async () => {
    setAttempted(true);
    if (hasErrors) return;

    let imageUrl = values.imageUrl;
    if (values.imageFile) {
      setIsUploadingImage(true);
      try {
        imageUrl = await uploadImageToS3(
          values.imageFile,
          POINT_REWARD_IMAGE_UPLOAD_FOLDER,
          values.imageFile.name
        );
        // La imagen ya está en S3: si el guardado falla, el reintento usa esta URL en vez
        // de subir otra copia que quedaría sin uso.
        const uploadedFile = values.imageFile;
        const uploadedUrl = imageUrl;
        setValues((previous) =>
          previous.imageFile === uploadedFile
            ? { ...previous, imageFile: null, imageUrl: uploadedUrl }
            : previous
        );
      } catch {
        toast.error(IMAGE_UPLOAD_ERROR_MESSAGE);
        return;
      } finally {
        setIsUploadingImage(false);
      }
    }

    const valuesToSave: PointRewardFormValues = { ...values, imageUrl };
    try {
      if (reward) {
        const updateChanges = buildPointRewardChanges(reward, valuesToSave);
        if (Object.keys(updateChanges).length === 0) return;
        await updatePointReward({ id: reward.id, changes: updateChanges }).unwrap();
        toast.success(UPDATE_SUCCESS_TOAST);
      } else {
        const payload = buildCreatePointRewardPayload(valuesToSave, tenantId);
        if (payload === null) return;
        await createPointReward(payload).unwrap();
        toast.success(CREATE_SUCCESS_TOAST);
      }
      onHide();
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? SAVE_FALLBACK_MESSAGE);
    }
  };

  // Lo que se ve: el archivo elegido (local) o la imagen que queda guardada.
  const previewImageSrc = localPreviewUrl ?? values.imageUrl;
  const imageHint = values.imageFile
    ? `Pendiente de guardar: ${values.imageFile.name}`
    : reward?.imageUrl && values.imageUrl === null
      ? IMAGE_REMOVE_HINT
      : IMAGE_HINT;

  return (
    <AppDialog
      variant="form"
      visible={visible}
      onHide={onHide}
      header={reward ? `Editar premio: ${reward.title}` : "Crear premio"}
      primaryLabel={reward ? "Guardar cambios" : "Crear premio"}
      onPrimary={handleSave}
      loading={isSaving}
      primaryDisabled={primaryDisabled}
      className="neutral-input-controls"
      style={{ width: "min(92vw, 880px)" }}
    >
      <div className="grid">
        <div className="col-12 md:col-7">
          <FormRow>
            <FormToggle label="Activo" htmlFor="pointRewardActive">
              <InputSwitch
                inputId="pointRewardActive"
                checked={values.active}
                onChange={(event) => setField("active", event.value)}
              />
            </FormToggle>
          </FormRow>
          {/* Apagarlo desde acá es una baja igual que la de la tabla: mismo aviso de QR vivos. */}
          {reward !== null &&
            reward.active &&
            !values.active &&
            reward.pendingRedemptionCount > 0 && (
              <Message
                severity="warn"
                className="w-full justify-content-start mb-3"
                text={formatPendingRedemptionsNotice(reward.pendingRedemptionCount)}
              />
            )}

          <BlockTitle>Premio</BlockTitle>
          <FormRow>
            <FormField label="Título" htmlFor="pointRewardTitle" required error={shownErrors.title}>
              <InputText
                id="pointRewardTitle"
                placeholder="Ej: Café gratis en el buffet"
                value={values.title}
                maxLength={200}
                onChange={(event) => setField("title", event.target.value)}
                className="w-full"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Descripción" htmlFor="pointRewardDescription">
              <InputTextarea
                id="pointRewardDescription"
                placeholder="Ej: Un café chico en el buffet del club, de lunes a viernes"
                value={values.description}
                maxLength={2000}
                rows={3}
                onChange={(event) => setField("description", event.target.value)}
                className="w-full"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              label="Tipo de premio"
              htmlFor="pointRewardType"
              required
              error={shownErrors.rewardType}
            >
              <Dropdown
                appendTo="self"
                inputId="pointRewardType"
                value={values.rewardType}
                options={REWARD_TYPE_OPTIONS}
                optionLabel="label"
                optionValue="value"
                placeholder="Elegí el tipo de premio"
                onChange={(event) => setField("rewardType", event.value)}
                className="w-full"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Imagen">
              {previewImageSrc && (
                <img
                  src={previewImageSrc}
                  alt="Imagen del premio"
                  className="border-1 surface-border"
                  style={IMAGE_THUMBNAIL_STYLE}
                />
              )}
              <div className="flex flex-wrap align-items-center gap-2">
                <Button
                  type="button"
                  label={previewImageSrc ? "Cambiar imagen" : "Subir imagen"}
                  icon="pi pi-upload"
                  outlined
                  className="w-auto"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isSaving}
                />
                {previewImageSrc && (
                  <Button
                    type="button"
                    label="Quitar"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    className="w-auto"
                    // Primero descarta el archivo nuevo sin guardar; si no hay, quita la imagen guardada.
                    onClick={() =>
                      values.imageFile ? setField("imageFile", null) : setField("imageUrl", null)
                    }
                    disabled={isSaving}
                  />
                )}
                <small className="text-600">{imageHint}</small>
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept={IMAGE_FILE_ACCEPT}
                style={{ display: "none" }}
                data-testid="point-reward-image-input"
                onChange={handleImageSelect}
              />
            </FormField>
          </FormRow>

          <BlockTitle>Costo y stock</BlockTitle>
          <FormRow>
            <FormField
              label="Costo en puntos"
              htmlFor="pointRewardCost"
              required
              error={shownErrors.pointsCost}
            >
              <InputNumber
                inputId="pointRewardCost"
                placeholder="Ej: 50"
                value={values.pointsCost}
                onChange={handlePointsCostChange}
                onValueChange={handlePointsCostChange}
                min={MIN_POINT_REWARD_COST}
                max={MAX_POINT_REWARD_COST}
                useGrouping={false}
                maxFractionDigits={0}
                className="w-full"
              />
            </FormField>
            <FormField
              label="Stock"
              htmlFor="pointRewardStock"
              required={!values.unlimitedStock}
              error={shownErrors.stock}
              hint={STOCK_HINT}
            >
              <InputNumber
                inputId="pointRewardStock"
                value={values.stock}
                onChange={handleStockChange}
                onValueChange={handleStockChange}
                min={0}
                useGrouping={false}
                maxFractionDigits={0}
                placeholder={values.unlimitedStock ? "Ilimitado" : "Ej: 20"}
                className="w-full"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormToggle label="Stock ilimitado" htmlFor="pointRewardUnlimitedStock">
              <InputSwitch
                inputId="pointRewardUnlimitedStock"
                checked={values.unlimitedStock}
                onChange={(event) => setUnlimitedStock(event.value)}
              />
            </FormToggle>
          </FormRow>
          <FormRow>
            <FormField
              label="Horas de expiración del QR"
              htmlFor="pointRewardQrExpirationHours"
              required
              error={shownErrors.qrExpirationHours}
              hint={QR_EXPIRATION_HINT}
            >
              <InputNumber
                inputId="pointRewardQrExpirationHours"
                placeholder="Ej: 72"
                value={values.qrExpirationHours}
                onChange={handleQrExpirationHoursChange}
                onValueChange={handleQrExpirationHoursChange}
                min={MIN_QR_EXPIRATION_HOURS}
                max={MAX_QR_EXPIRATION_HOURS}
                useGrouping={false}
                maxFractionDigits={0}
                className="w-full"
              />
            </FormField>
          </FormRow>

          <BlockTitle>Partner</BlockTitle>
          <FormRow>
            <FormField
              label="Partner"
              htmlFor="pointRewardPartner"
              hint={legacyPartnerHint}
            >
              <Dropdown
                appendTo="self"
                inputId="pointRewardPartner"
                value={values.partnerId}
                options={partnerOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Sin partner"
                filter
                filterPlaceholder="Buscar partner"
                emptyMessage="No hay partners todavía"
                emptyFilterMessage="No se encontraron partners"
                loading={isLoadingPartners}
                showClear
                onChange={(event) => setField("partnerId", event.value ?? null)}
                className="w-full"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Ubicación del partner" htmlFor="pointRewardPartnerLocation">
              <InputTextarea
                id="pointRewardPartnerLocation"
                placeholder="Ej: Buffet de la sede central, planta baja"
                value={values.partnerLocationInfo}
                maxLength={2000}
                rows={3}
                onChange={(event) => setField("partnerLocationInfo", event.target.value)}
                className="w-full"
              />
            </FormField>
          </FormRow>
        </div>

        <div className="col-12 md:col-5">
          <PointRewardPreview
            title={normalizeText(values.title)}
            partnerName={selectedPartnerName}
            pointsCost={values.pointsCost}
            stock={values.unlimitedStock ? null : values.stock}
            imageUrl={previewImageSrc}
            active={values.active}
          />
        </div>
      </div>

      {reward && <Message severity="info" text={EDIT_NOTICE} className="w-full mt-3" />}
    </AppDialog>
  );
};
